import type { DateTime } from 'luxon';
import { action, makeObservable, observable } from 'mobx';

import type { DraftTimelog, WeeklyWorkLog } from '@/types';

import type { CalendarEventWithColor, CollapsedLogGroup, LogFormDefaults } from '../../types';

export interface DragLogData {
  date: DateTime;
  startTime: string;
  duration: string;
}

export class CalendarUIStateModel {
  selectedEvent: CalendarEventWithColor | null = null;
  selectedLogGroup: CollapsedLogGroup | null = null;
  editingLog: WeeklyWorkLog | null = null;
  selectedDraft: DraftTimelog | null = null;

  logFormEvent: CalendarEventWithColor | null = null;
  logFormDefaults: LogFormDefaults | null = null;
  logFormTaskLabel = '';
  logFormForAll = false;

  quickAddDate: DateTime | null = null;
  dragLogData: DragLogData | null = null;
  dragCreateDraftData: DragLogData | null = null;

  constructor() {
    makeObservable(this, {
      selectedEvent: observable,
      selectedLogGroup: observable,
      editingLog: observable,
      selectedDraft: observable,
      logFormEvent: observable,
      logFormDefaults: observable,
      logFormTaskLabel: observable,
      logFormForAll: observable,
      quickAddDate: observable,
      dragLogData: observable,
      dragCreateDraftData: observable,
      selectEvent: action.bound,
      clearSelectedEvent: action.bound,
      selectLogGroup: action.bound,
      clearLogGroup: action.bound,
      openEditLog: action.bound,
      closeEditLog: action.bound,
      openQuickAdd: action.bound,
      closeQuickAdd: action.bound,
      openDragLog: action.bound,
      closeDragLog: action.bound,
      openDraftEdit: action.bound,
      closeDraftEdit: action.bound,
      openDragCreateDraft: action.bound,
      closeDragCreateDraft: action.bound,
      setLogFormState: action.bound,
      clearLogFormState: action.bound,
    });
  }

  selectEvent(event: CalendarEventWithColor): void {
    this.selectedEvent = event;
  }

  clearSelectedEvent(): void {
    this.selectedEvent = null;
  }

  selectLogGroup(group: CollapsedLogGroup): void {
    this.selectedLogGroup = group;
  }

  clearLogGroup(): void {
    this.selectedLogGroup = null;
  }

  openEditLog(log: WeeklyWorkLog): void {
    this.editingLog = log;
  }

  closeEditLog(): void {
    this.editingLog = null;
  }

  openQuickAdd(date: DateTime): void {
    this.quickAddDate = date;
  }

  closeQuickAdd(): void {
    this.quickAddDate = null;
  }

  openDragLog(date: DateTime, startTime: string, duration: string): void {
    this.dragLogData = { date, startTime, duration };
  }

  closeDragLog(): void {
    this.dragLogData = null;
  }

  openDraftEdit(draft: DraftTimelog): void {
    this.selectedDraft = draft;
  }

  closeDraftEdit(): void {
    this.selectedDraft = null;
  }

  openDragCreateDraft(date: DateTime, startTime: string, duration: string): void {
    this.dragCreateDraftData = { date, startTime, duration };
  }

  closeDragCreateDraft(): void {
    this.dragCreateDraftData = null;
  }

  setLogFormState(
    event: CalendarEventWithColor,
    defaults: LogFormDefaults,
    taskLabel: string,
    forAll: boolean,
  ): void {
    this.logFormEvent = event;
    this.logFormDefaults = defaults;
    this.logFormTaskLabel = taskLabel;
    this.logFormForAll = forAll;
    this.clearSelectedEvent();
  }

  clearLogFormState(): void {
    this.logFormEvent = null;
    this.logFormDefaults = null;
    this.logFormTaskLabel = '';
    this.logFormForAll = false;
  }
}
